import type { Page } from '@inertiajs/core';
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import {
    buildToolPageJsonLd,
    ogAlternateLocale,
    ogPrimaryLocale,
} from '@/lib/seoJsonLd';

export type ToolPageSeoInput = {
    /** e.g. `tools.pdf.merge.seo` — each page passes its own literal. */
    seoPrefix: string;
    /** Translation key for short UI title if `head_title` is missing. */
    fallbackTitleKey: string;
    fallbackTitleLabel: string;
    t: (key: string, fallback?: string) => string;
    page: Page;
    /** `dashboard.url()` — locale-prefixed path starting with `/`. */
    homeRelativeUrl: string;
    /** Current tool `*.url()` — path starting with `/`. */
    selfRelativeUrl: string;
    /** When false, JSON-LD omits BreadcrumbList (e.g. site home). */
    includeBreadcrumbs?: boolean;
};

export type ToolPageSeoResult = {
    headTitle: ComputedRef<string>;
    metaDescription: ComputedRef<string>;
    ogTitle: ComputedRef<string>;
    ogDescription: ComputedRef<string>;
    absolutePageUrl: ComputedRef<string>;
    ogLocale: ComputedRef<string>;
    ogLocaleAlternate: ComputedRef<string>;
    jsonLd: ComputedRef<string>;
    siteDisplayName: ComputedRef<string>;
};

/**
 * Shared wiring for absolute URLs, OG locales, and JSON-LD graph shape.
 * SEO copy always comes from lang keys under `seoPrefix`; callers pass a unique prefix per page.
 */
export function useToolPageSeo(input: ToolPageSeoInput): ToolPageSeoResult {
    const includeBreadcrumbs = input.includeBreadcrumbs !== false;
    const absoluteBaseUrl = computed(() =>
        String(input.page.props.absoluteBaseUrl ?? '').replace(/\/$/, ''),
    );

    const pathOnly = computed(() => {
        const raw = String(input.page.url ?? '');
        const q = raw.split('?')[0] ?? raw;

        return q.startsWith('/') ? q : `/${q}`;
    });

    const absolutePageUrl = computed(
        () => `${absoluteBaseUrl.value}${pathOnly.value}`,
    );

    const p = (suffix: string) => `${input.seoPrefix}.${suffix}`;

    const headTitle = computed(() =>
        input.t(
            p('head_title'),
            input.t(input.fallbackTitleKey, input.fallbackTitleLabel),
        ),
    );

    const metaDescription = computed(() => input.t(p('meta_description'), ''));

    const ogTitle = computed(() => input.t(p('og_title'), headTitle.value));

    const ogDescription = computed(() =>
        input.t(p('og_description'), metaDescription.value),
    );

    const siteDisplayName = computed(() =>
        String(input.page.props.siteDisplayName ?? ''),
    );

    const ogLocale = computed(() => ogPrimaryLocale(input.page.props.locale));

    const ogLocaleAlternate = computed(() =>
        ogAlternateLocale(input.page.props.locale),
    );

    const siteHomeUrl = computed(
        () => `${absoluteBaseUrl.value}${input.homeRelativeUrl}`,
    );

    const jsonLd = computed(() =>
        buildToolPageJsonLd({
            pageUrl: absolutePageUrl.value,
            applicationName: ogTitle.value,
            applicationDescription: metaDescription.value,
            siteName: siteDisplayName.value,
            siteHomeUrl: siteHomeUrl.value,
            includeWebSite: !includeBreadcrumbs,
            breadcrumbs: includeBreadcrumbs
                ? [
                      {
                          name: input.t('tools.nav.home', 'Home'),
                          item: siteHomeUrl.value,
                      },
                      {
                          name: ogTitle.value,
                          item: `${absoluteBaseUrl.value}${input.selfRelativeUrl}`,
                      },
                  ]
                : [],
        }),
    );

    return {
        headTitle,
        metaDescription,
        ogTitle,
        ogDescription,
        absolutePageUrl,
        ogLocale,
        ogLocaleAlternate,
        jsonLd,
        siteDisplayName,
    };
}

export function useToolPageSeoFromInertia(
    args: Omit<ToolPageSeoInput, 'page'>,
): ToolPageSeoResult {
    return useToolPageSeo({ ...args, page: usePage() });
}
